// Tarjeta principal de la confirmación de la tienda online (confirmación v2).
//
// Pura: del estimado con el que se pagó (PR K, `WaitEstimate`) y la hora del
// pedido, a la HORA DEL DÍA que se le promete al cliente. No recalcula el
// estimado: solo lo suma a la hora del pedido. Domicilio = cocina + repartidor
// (`withCourier`, el mismo que usa el texto en minutos).
//
// Formato: Intl con locale explícito (es-US / en-US) y la zona del LOCAL, igual
// que `lib/orderTracking/formatTime.ts`. `formatRange` comparte el "PM":
// "6:25 – 6:35 PM", no "6:25 PM – 6:35 PM".
import { withCourier, type WaitEstimate } from "@/lib/microsite/waitEstimate"

export type ConfirmHero =
  /** En vivo: rango "6:25 – 6:35 PM". */
  | { kind: "range"; text: string }
  /** Fijo (bandera apagada): un solo valor "~6:30 PM". */
  | { kind: "approx"; text: string }
  /** En vivo con alta demanda: la hora del tope del rango. */
  | { kind: "busy"; text: string }
  /** Programado: la hora que eligió el cliente, y el día aparte. */
  | { kind: "scheduled"; text: string; day: string }

function tag(locale: string): string {
  return locale === "en" ? "en-US" : "es-US"
}

function clockFormat(locale: string, timeZone: string): Intl.DateTimeFormat {
  const opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" }
  try {
    return new Intl.DateTimeFormat(tag(locale), { ...opts, timeZone })
  } catch {
    // Zona inválida en la fila del local: mejor UTC que un crash.
    return new Intl.DateTimeFormat(tag(locale), { ...opts, timeZone: "UTC" })
  }
}

function safeZone(timeZone: string | null | undefined): string {
  if (timeZone) return timeZone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

export function confirmHero(args: {
  estimate: WaitEstimate
  /** Hora del pedido (ms). */
  placedAtMs: number
  /** Domicilio: minutos del repartidor (de la cotización), o null. */
  courierMinutes: number | null
  delivery: boolean
  scheduledFor: string | null
  timeZone: string | null | undefined
  locale: string
}): ConfirmHero {
  const zone = safeZone(args.timeZone)
  const fmt = clockFormat(args.locale, zone)

  if (args.scheduledFor) {
    const at = new Date(args.scheduledFor)
    let day: string
    try {
      day = new Intl.DateTimeFormat(tag(args.locale), { weekday: "long", day: "numeric", month: "short", timeZone: zone }).format(at)
    } catch {
      day = new Intl.DateTimeFormat(tag(args.locale), { weekday: "long", day: "numeric", month: "short", timeZone: "UTC" }).format(at)
    }
    return { kind: "scheduled", text: fmt.format(at), day }
  }

  const est = args.delivery ? withCourier(args.estimate, args.courierMinutes) : args.estimate
  const at = (min: number) => new Date(args.placedAtMs + min * 60_000)

  if (est.mode === "fixed") return { kind: "approx", text: `~${fmt.format(at(est.minutes))}` }
  if (est.highDemand) return { kind: "busy", text: fmt.format(at(est.high)) }
  const a = at(est.low)
  const b = at(est.high)
  // formatRange existe en todos los navegadores que soporta la tienda; el
  // respaldo arma el rango a mano por si acaso.
  const text =
    typeof (fmt as Intl.DateTimeFormat & { formatRange?: unknown }).formatRange === "function"
      ? fmt.formatRange(a, b)
      : `${fmt.format(a)} – ${fmt.format(b)}`
  return { kind: "range", text: tidyRange(text) }
}

/**
 * Cada motor ICU pone espacios distintos alrededor de la raya (nada, fino, sin
 * corte): se normalizan a "6:25 – 6:35 PM" para que se lea igual en todos.
 */
function tidyRange(s: string): string {
  return s.replace(/[\u2009\u202F\u00A0]/g, " ").replace(/\s*[–-]\s*/, " – ").replace(/\s+/g, " ").trim()
}
